/**
 * useCatalogCache Hook
 * Manages catalog data caching with TTL
 */

import { useCallback, useRef } from 'react';
import { cacheConfig, getCachedData, setCacheData, invalidateCache } from '@/lib/config/cacheConfig';
import type { ListingCard } from '@/lib/api';

interface CachedCatalog {
  items: ListingCard[];
  hasMore: boolean;
  cursor: { createdAt: string; listingId: string } | null;
  filterKey: string;
}

const { ttlMs, storageKey } = cacheConfig.catalog;

/**
 * Generate a cache key based on active filters
 */
function getFilterKey(params: {
  speciesKey: string | null;
  breedIds: string[];
  sex: string[];
  ageBuckets: string[];
  geoPlaceId: string | null;
  searchQuery: string;
  sort: string;
  isForYouMode: boolean;
}): string {
  return JSON.stringify({
    species: params.speciesKey,
    breeds: params.breedIds.sort(),
    sex: params.sex.sort(),
    ages: params.ageBuckets.sort(),
    geo: params.geoPlaceId,
    search: params.searchQuery,
    sort: params.sort,
    forYou: params.isForYouMode,
  });
}

export function useCatalogCache() {
  const lastFetchTime = useRef<number>(0);

  /**
   * Check if we should fetch fresh data
   */
  const shouldFetch = useCallback((filterKey: string): boolean => {
    const cached = getCachedData<CachedCatalog>(storageKey, ttlMs);
    
    // No cache or different filters - should fetch
    if (!cached || cached.filterKey !== filterKey) {
      return true;
    }
    
    // Cache valid and same filters - don't fetch
    return false;
  }, []);

  /**
   * Get cached data if available and valid
   */
  const getCached = useCallback((filterKey: string): CachedCatalog | null => {
    const cached = getCachedData<CachedCatalog>(storageKey, ttlMs);
    
    if (cached && cached.filterKey === filterKey) {
      return cached;
    }
    
    return null;
  }, []);

  /**
   * Update cache with new data
   */
  const updateCache = useCallback((
    items: ListingCard[],
    hasMore: boolean,
    cursor: { createdAt: string; listingId: string } | null,
    filterKey: string,
    append = false
  ): void => {
    if (append) {
      const existing = getCachedData<CachedCatalog>(storageKey, ttlMs);
      if (existing && existing.filterKey === filterKey) {
        setCacheData<CachedCatalog>(storageKey, {
          items: [...existing.items, ...items],
          hasMore,
          cursor,
          filterKey,
        });
        return;
      }
    }
    
    setCacheData<CachedCatalog>(storageKey, {
      items,
      hasMore,
      cursor,
      filterKey,
    });
    lastFetchTime.current = Date.now();
  }, []);

  /**
   * Clear cache (force refresh on next load)
   */
  const clearCache = useCallback((): void => {
    invalidateCache(storageKey);
    lastFetchTime.current = 0;
  }, []);

  return {
    shouldFetch,
    getCached,
    updateCache,
    clearCache,
    getFilterKey,
  };
}

export default useCatalogCache;
